import { lockedRecordHint } from '@till-erp/collab/values';
import { Lock } from 'lucide-react';
import { cn } from '../../lib/utils.ts';
import { Tooltip } from './tooltip.tsx';

/** Text eines gesperrten Bezugs: „<Art> <Nummer>“, ohne Nummer nur die Art (Story 23.17). */
export function lockedRecordText(record: { entityLabel: string | null; entityName: string | null }): string {
  return `${record.entityLabel ?? ''} ${record.entityName ?? ''}`.trim();
}

/**
 * Gesperrter Bezug einer Aufgabe (Story 23.17, Rolle Mitarbeiter): Schloss + „Auftrag 202724536“ – nie ein Link, nie der
 * Name des Datensatzes. Das Schloss erklärt per Tooltip, warum nur die Nummer zu sehen ist (Maus, Fingertipp und
 * Tastatur: das Schloss ist mit Tab erreichbar und zeigt den Tooltip beim Fokus); der Text kommt aus `lockedRecordHint`
 * (collab) und ist zugleich der Name des Schlosses für Screenreader. Das Schloss liegt über der Klickfläche der Zeile
 * (`relative z-10`), sonst verdeckt deren `::after` den Hover. Ein Tipp aufs Schloss erklärt nur (Tooltip) und öffnet
 * nicht zugleich die Zeile bzw. Karte darunter (der Klick endet am Schloss).
 *
 * `focusable={false}`: in einem Knopf (Zeile der Aufgabenliste öffnet das Detail-Blatt) – dort darf nichts eigens
 * fokussierbar sein (HTML: kein Bedienelement im Knopf); der Satz steht dann unsichtbar im Namen des Knopfs, und das
 * Detail-Blatt zeigt dasselbe Schloss fokussierbar.
 */
export function LockedRecord({
  entityType,
  entityLabel,
  entityName,
  className,
  testId = 'gesperrter-bezug',
  focusable = true,
}: {
  entityType: string | null;
  entityLabel: string | null;
  entityName: string | null;
  className?: string;
  testId?: string;
  focusable?: boolean;
}) {
  const hint = lockedRecordHint(entityType);
  return (
    <span data-testid={testId} data-gesperrt="" className={cn('inline-flex min-w-0 items-center gap-space-1', className)}>
      <Tooltip label={hint} side="bottom" touch className="relative z-10 flex-none">
        <span
          data-testid="bezug-schloss"
          className={cn('inline-flex text-ink-3', focusable && 'rounded-full')}
          onClick={(e) => e.stopPropagation()}
          {...(focusable ? { tabIndex: 0, role: 'img', 'aria-label': hint } : {})}
        >
          <Lock size={13} strokeWidth={1.75} aria-hidden />
        </span>
      </Tooltip>
      <span className="min-w-0 truncate">{lockedRecordText({ entityLabel, entityName })}</span>
      {focusable ? null : <span className="sr-only">{` (${hint})`}</span>}
    </span>
  );
}
